import pdfMake from 'pdfmake/build/pdfmake';
// import { TFontDictionary } from 'pdfmake/interfaces';

// Define types for pdfMake internals that aren't exposed in the public types
// interface PdfMakeInternal {
//   vfs?: Record<string, any>;
//   fonts?: TFontDictionary;
//   version?: string;
// }

/**
 * Debug utility to check the current state of pdfMake configuration
 * @returns Object containing debug information about pdfMake
 */
export const debugPdfMakeConfig = () => {
  const vfsKeys = pdfMake.vfs ? Object.keys(pdfMake.vfs) : [];
  const fontKeys = pdfMake.fonts ? Object.keys(pdfMake.fonts) : [];
  
  const fontDetails: Record<string, any> = {};
  
  if (pdfMake.fonts) {
    for (const fontName of fontKeys) {
      fontDetails[fontName] = pdfMake.fonts[fontName];
    }
  }
  
  const vfsSamples: Record<string, string> = {};
  if (pdfMake.vfs) {
    for (const key of vfsKeys) {
      const value = pdfMake.vfs[key];
      if (typeof value === 'string') {
        vfsSamples[key] = value.length > 0 
          ? `${value.substring(0, 20)}... (${value.length} chars)` 
          : '(empty string)';
      } else {
        vfsSamples[key] = `(not a string: ${typeof value})`;
      }
    }
  }
  
  return {
    vfsAvailable: !!pdfMake.vfs,
    fontsAvailable: !!pdfMake.fonts,
    vfsKeys,
    fontKeys,
    fontDetails,
    vfsSamples,
    pdfMakeVersion: (pdfMake as any).version || 'unknown'
  };
};

/**
 * Checks if the required fonts are properly configured in pdfMake
 * @returns Object with validation results
 */
export const validatePdfMakeFonts = () => {
  const results = {
    hasVfs: !!pdfMake.vfs,
    hasFonts: !!pdfMake.fonts,
    hasIBMPlexSans: false,
    hasNormalStyle: false,
    hasBoldStyle: false,
    vfsHasRegularFont: false,
    vfsHasBoldFont: false,
    isValid: false,
    issues: [] as string[]
  };
  
  // Check if VFS exists
  if (!results.hasVfs) {
    results.issues.push('pdfMake.vfs is not defined');
    return results;
  }
  
  // Check if fonts configuration exists
  if (!results.hasFonts) {
    results.issues.push('pdfMake.fonts is not defined');
    return results;
  }
  
  // Check if IBMPlexSans font is defined
  if (pdfMake.fonts && 'IBMPlexSans' in pdfMake.fonts) {
    results.hasIBMPlexSans = true;
    
    const ibmFont = pdfMake.fonts['IBMPlexSans'];
    
    // Check if normal style is defined
    if (ibmFont.normal) {
      results.hasNormalStyle = true;
      
      // Check if the referenced font file exists in VFS
      const normalFontName = String(ibmFont.normal);
      if (pdfMake.vfs && normalFontName in pdfMake.vfs) {
        results.vfsHasRegularFont = true;
      } else {
        results.issues.push(`Font file '${normalFontName}' referenced in IBMPlexSans.normal is not in VFS`);
      }
    } else {
      results.issues.push('IBMPlexSans font is missing normal style definition');
    }
    
    // Check if bold style is defined
    if (ibmFont.bold) {
      results.hasBoldStyle = true;
      
      // Check if the referenced font file exists in VFS
      const boldFontName = String(ibmFont.bold);
      if (pdfMake.vfs && boldFontName in pdfMake.vfs) {
        results.vfsHasBoldFont = true;
      } else {
        results.issues.push(`Font file '${boldFontName}' referenced in IBMPlexSans.bold is not in VFS`);
      }
    } else {
      results.issues.push('IBMPlexSans font is missing bold style definition');
    }
  } else {
    results.issues.push('IBMPlexSans font is not defined in pdfMake.fonts');
  }
  
  // Check if everything is valid
  results.isValid = results.hasVfs && 
                    results.hasFonts && 
                    results.hasIBMPlexSans && 
                    results.hasNormalStyle && 
                    results.hasBoldStyle && 
                    results.vfsHasRegularFont && 
                    results.vfsHasBoldFont;
  
  return results;
};
